import { Form, InputGroup, Dropdown } from "react-bootstrap";
import { BsSearch, BsSortDown } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import { ExplorerConnectionLimitCounter } from "../Connections/ExplorerConnectionLimitCounter";
import { PAGE_SIZE } from "./eventAttendeesHelpers";

export interface EventAttendeesToolbarProps {
  eventName?: string;
  showPagination: boolean;
  currentPage: number;
  filteredCount: number;
  searchTerm: string;
  searchTargetLang: string;
  onSearchChange: (value: string) => void;
  onSortChange: (key: string) => void;
  showMobileSort: boolean;
  onMobileSortToggle: (nextShow: boolean) => void;
}

function EventAttendeesToolbar({
  eventName,
  showPagination,
  currentPage,
  filteredCount,
  searchTerm,
  searchTargetLang,
  onSearchChange,
  onSortChange,
  showMobileSort,
  onMobileSortToggle,
}: EventAttendeesToolbarProps) {
  const { t } = useTranslation();

  const sortMenu = (
    <Dropdown.Menu>
      <Dropdown.Item eventKey="connections.sort.recent">
        {t("connections.sort.recent")}
      </Dropdown.Item>
      <Dropdown.Item eventKey="connections.sort.firstName">
        {t("connections.sort.firstName")}
      </Dropdown.Item>
      <Dropdown.Item eventKey="connections.sort.lastName">
        {t("connections.sort.lastName")}
      </Dropdown.Item>
    </Dropdown.Menu>
  );

  return (
    <>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
        <div>
          <h5 className="fw-bold mb-0 text-dark">
            {t("events.attendeesTitle")} {eventName ? `- ${eventName}` : ""}
          </h5>
          <p className="text-muted small mb-0 mt-1">
            {showPagination
              ? t("events.attendeesRange", {
                  start: (currentPage - 1) * PAGE_SIZE + 1,
                  end: Math.min(currentPage * PAGE_SIZE, filteredCount),
                  total: filteredCount,
                })
              : t("events.attendeesCount", { count: filteredCount })}
          </p>
        </div>
      </div>

      <ExplorerConnectionLimitCounter className="mb-3" />

      <div className="d-flex d-md-none align-items-center gap-2 mb-3">
        <InputGroup className="rounded-2 flex-grow-1 min-w-0">
          <InputGroup.Text className="bg-white border-end-0 rounded-start">
            <BsSearch size={14} className="text-secondary" />
          </InputGroup.Text>
          <Form.Control
            placeholder={t("events.searchByName")}
            lang={searchTargetLang}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="border-start-0 rounded-end bg-white"
            style={{ minWidth: 0, fontSize: 14 }}
          />
        </InputGroup>
        <div className="d-flex gap-2 ms-auto flex-shrink-0">
          <Dropdown
            show={showMobileSort}
            onToggle={(nextShow) => {
              onMobileSortToggle(nextShow);
            }}
            onSelect={(k) => k && onSortChange(k)}
          >
            <Dropdown.Toggle
              variant="outline-primary"
              className="rounded-2 shadow-none d-flex align-items-center gap-2"
            >
              <BsSortDown size={16} />
              <span>{t("connections.sort")}</span>
            </Dropdown.Toggle>
            {sortMenu}
          </Dropdown>
        </div>
      </div>

      <div className="d-none d-md-flex flex-row gap-2 mb-3">
        <InputGroup className="rounded-2 flex-grow-1">
          <InputGroup.Text className="bg-white border-end-0 rounded-start">
            <BsSearch size={16} className="text-secondary" />
          </InputGroup.Text>
          <Form.Control
            placeholder={t("events.searchByName")}
            lang={searchTargetLang}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="border-start-0 rounded-end bg-white"
          />
        </InputGroup>
        <Dropdown onSelect={(k) => k && onSortChange(k)}>
          <Dropdown.Toggle
            variant="outline-primary"
            className="rounded-2 shadow-none d-flex align-items-center gap-2"
          >
            <BsSortDown size={16} />
            <span>{t("connections.sort")}</span>
          </Dropdown.Toggle>
          {sortMenu}
        </Dropdown>
      </div>
    </>
  );
}

export default EventAttendeesToolbar;
